import React, { useState } from 'react';
import { useParams, Link } from 'react-router';
import { DGPTAlertRun } from '../../types/dgptAlerts';
import { useAlertConfigById } from './hooks/useAlertConfigs';
import { useAlertRuns } from './hooks/useAlertRuns';
import { useAlertEventInstances } from './hooks/useAlertEventInstances';
import PageTitle from '../headers/PageTitle';
import { Spinner } from 'react-bootstrap';
import StylishNewTable from '../../components/DesignSystems/New/StylishNewTable';

interface TableColumn {
  dataField: string;
  text: string;
  sort: boolean;
  formatter?: (cell: unknown, row?: DGPTAlertRun) => React.ReactNode;
}

const AlertRunTable: React.FC = () => {
  const { alert_config_id: alertConfigId } = useParams<{
    alert_config_id: string;
  }>();
  const { data: config, isLoading: isLoadingConfig } = useAlertConfigById(
    alertConfigId || ''
  );
  const { data: runs, isLoading: isLoadingRuns } = useAlertRuns({
    configId: alertConfigId || '',
  });
  const { data: instances } = useAlertEventInstances(alertConfigId || '');
  const [_selectedRun, _setSelectedRun] = useState<DGPTAlertRun | null>(null);
  const [_showDetailsModal, _setShowDetailsModal] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const PER_PAGE = 10;

  const columns: TableColumn[] = [
    {
      dataField: 'status',
      text: 'Status',
      sort: true,
      formatter: (cell: unknown) => (
        <span
          className={`badge bg-${(cell as string) === 'Completed' ? 'success' : (cell as string) === 'Failed' ? 'danger' : 'warning'}`}
        >
          {cell as string}
        </span>
      ),
    },
    {
      dataField: 'createdAt',
      text: 'Created At',
      sort: true,
      formatter: (cell: unknown) => new Date(cell as string).toLocaleString(),
    },
    {
      dataField: 'systemTriggered',
      text: 'Trigger Type',
      sort: true,
      formatter: (cell: unknown) => ((cell as boolean) ? 'System' : 'Manual'),
    },
    {
      dataField: 'dgptAlertEventInstanceId',
      text: 'SITREP',
      sort: true,
      formatter: (cell: unknown) => {
        if (!cell) return 'No SITREP';
        const instance = instances?.find((i) => i.id === cell);
        if (!instance?.dgptSitrepId) return 'No SITREP';
        return (
          <Link to={`/sitreps/${instance.dgptSitrepId}`}>View SITREP</Link>
        );
      },
    },
  ];

  if (isLoadingConfig || isLoadingRuns) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!config) {
    return <div>Alert configuration not found</div>;
  }

  const filteredRuns: DGPTAlertRun[] = runs || [];
  const hasRuns = filteredRuns.length > 0;

  return (
    <div className="p-4 pt-0">
      <PageTitle
        title={`Alert Logs - ${config.name}`}
        parents={['Alert Configurations', config?.name || 'Loading...']}
      />
      <p className="text-muted mb-4">
        Each time DisasterGPT checks for alert conditions, a log entry is
        created. When conditions match a trigger, a{' '}
        <Link to={`/aialerts/${alertConfigId}/reports`}>Report</Link> is
        generated and a <Link to="/sitreps">SITREP</Link> is created or updated.
        The logs show the history of all checks for this Configuration, whether
        they resulted in an alert or not.
      </p>
      <div className="bg-neutral-800 rounded-1 p-3">
        {!hasRuns && (
          <div className="alert alert-info mb-3">
            <strong>No Alert Logs Found</strong>
            <p className="mb-0">There are no alert logs to display.</p>
          </div>
        )}

        {hasRuns && (
          <StylishNewTable
            keyField="id"
            columns={columns}
            rows={filteredRuns}
            pagination
            page={page}
            perPage={PER_PAGE}
            total={filteredRuns.length}
            onPageChange={setPage}
            noDataIndication="No alert logs found"
          />
        )}
      </div>
    </div>
  );
};

export default AlertRunTable;
